import { useCallback, useEffect, useRef, useState, type FocusEvent, type KeyboardEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight, Cloud, Pin, PinOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { navGroups } from './nav-groups';
import { findActiveNavGroup, isNavItemActive } from './nav-matching';
import { readSidebarPinnedPreference, writeSidebarPinnedPreference } from './sidebar-preferences';
import { MASTER_MAIN_CONTENT_ID } from './master-layout-ids';

/** Ritardo anti-flicker prima di richiudere la sidebar quando il puntatore esce. */
const COLLAPSE_DELAY_MS = 150;

/**
 * Sidebar desktop (≥760px) del Master Control.
 * - Chiusa a rail (76px) per default: solo le icone dei gruppi.
 * - Si espande (252px) al passaggio del mouse o al focus da tastiera, in
 *   overlay sul contenuto (la rail conserva 76px nel flusso), e si richiude
 *   da sola quando il puntatore esce.
 * - Il pin in testata la blocca aperta: a quel punto occupa 252px nel flusso
 *   e la scelta è persistita in localStorage.
 * Sotto 760px la navigazione è affidata a `MasterMobileNav`.
 */
export default function MasterSidebar(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [pinned, setPinned] = useState<boolean>(() => readSidebarPinnedPreference());
  const [hovered, setHovered] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeGroup = findActiveNavGroup(location.pathname, navGroups);
  const expanded = pinned || hovered || focusWithin;

  const cancelScheduledCollapse = useCallback(() => {
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = null;
    }
  }, []);

  const scheduleCollapse = useCallback(() => {
    cancelScheduledCollapse();
    collapseTimerRef.current = setTimeout(() => {
      collapseTimerRef.current = null;
      setHovered(false);
    }, COLLAPSE_DELAY_MS);
  }, [cancelScheduledCollapse]);

  useEffect(() => () => cancelScheduledCollapse(), [cancelScheduledCollapse]);

  const togglePinned = (): void => {
    setPinned((current) => {
      const next = !current;
      writeSidebarPinnedPreference(next);
      return next;
    });
  };

  const handleMouseEnter = (): void => {
    cancelScheduledCollapse();
    setHovered(true);
  };

  const handleFocus = (): void => {
    cancelScheduledCollapse();
    setFocusWithin(true);
  };

  const handleBlur = (event: FocusEvent<HTMLElement>): void => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setFocusWithin(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key !== 'Escape' || pinned || !expanded) return;
    event.preventDefault();
    cancelScheduledCollapse();
    setHovered(false);
    setFocusWithin(false);
    const mainContent = document.getElementById(MASTER_MAIN_CONTENT_ID);
    if (mainContent instanceof HTMLElement) {
      mainContent.focus({ preventScroll: true });
    } else if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const overlay = expanded && !pinned;

  return (
    <aside
      className={cn(
        'sticky top-0 hidden h-screen shrink-0 min-[760px]:block',
        'transition-[width] duration-200 motion-reduce:transition-none',
        pinned ? 'w-[252px]' : 'w-[76px]',
        overlay ? 'z-50' : 'z-30'
      )}
      aria-label={expanded ? 'Navigazione Master Control, menu espanso' : 'Navigazione Master Control, menu compatto'}
      data-state={expanded ? 'expanded' : 'collapsed'}
      data-pinned={pinned ? 'true' : 'false'}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={scheduleCollapse}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      <div
        className={cn(
          'absolute inset-y-0 left-0 flex h-full flex-col overflow-hidden border-r border-border/80',
          'dark:backdrop-blur-xl',
          // In overlay il pannello copre topbar e contenuto: sfondo opaco per non far trasparire il testo sottostante.
          overlay ? 'bg-white dark:bg-[#0e1a2c]' : 'bg-white/90 dark:bg-[#0e1a2c]/90',
          'transition-[width,box-shadow] duration-200 motion-reduce:transition-none',
          expanded ? 'w-[252px]' : 'w-[76px]',
          overlay && 'shadow-2xl shadow-purple-900/10 dark:shadow-black/40'
        )}
      >
        <div className="flex h-[76px] shrink-0 items-center border-b border-border/80">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className={cn(
              'flex h-full min-w-0 flex-1 items-center gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-purple-500',
              expanded ? 'justify-start pl-[18px] pr-2' : 'justify-center px-3'
            )}
            aria-label="Vai alla dashboard principale"
          >
            <img src={`${import.meta.env.BASE_URL}logos/easyflow-icon-master.svg`} alt="" aria-hidden="true" className="h-[38px] w-[38px] shrink-0" />
            {expanded && (
              <span className="min-w-0 leading-tight">
                <img src={`${import.meta.env.BASE_URL}logos/easyflow-word-master.svg`} alt="EasyFlow" className="h-5 w-auto" />
                <span className="mt-1 block whitespace-nowrap text-[8px] font-bold tracking-[0.16em] text-purple-700/70 dark:text-purple-300/80">MASTER CONTROL</span>
              </span>
            )}
          </button>
          {expanded && (
            <button
              type="button"
              onClick={togglePinned}
              className={cn(
                'mr-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500',
                'hover:bg-purple-100/70 dark:hover:bg-purple-500/15',
                pinned ? 'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-100' : 'text-muted-foreground dark:text-slate-300'
              )}
              aria-pressed={pinned}
              aria-label={pinned ? 'Sblocca menu' : 'Blocca menu aperto'}
              title={pinned ? 'Sblocca menu' : 'Blocca menu aperto'}
            >
              {pinned ? <PinOff className="h-4 w-4" aria-hidden="true" /> : <Pin className="h-4 w-4" aria-hidden="true" />}
            </button>
          )}
        </div>

        <nav
          className="custom-scrollbar min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-3 py-4"
          aria-label="Master Control"
          aria-expanded={expanded}
        >
          {navGroups.map((group) => {
            const GroupIcon = group.icon;
            const groupIsActive = activeGroup?.name === group.name;
            const firstItem = group.items[0];
            return (
              <section key={group.name} className={cn('mb-4', expanded && 'mb-3')} aria-label={group.name}>
                {expanded && (
                  <p className="whitespace-nowrap px-2 pb-1.5 text-[9px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{group.name}</p>
                )}
                <button
                  type="button"
                  onClick={() => { if (firstItem) navigate(firstItem.href); }}
                  className={cn(
                    'flex h-10 w-full items-center rounded-lg px-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500',
                    'hover:bg-purple-100/70 dark:hover:bg-purple-500/15',
                    expanded ? 'gap-3' : 'justify-center',
                    groupIsActive ? 'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-100' : 'text-muted-foreground dark:text-slate-300'
                  )}
                  aria-label={group.name}
                  aria-current={groupIsActive && !expanded ? 'true' : undefined}
                  title={group.name}
                >
                  <GroupIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  {expanded && (
                    <>
                      <span className="flex-1 truncate">{group.name}</span>
                      <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    </>
                  )}
                </button>
                {expanded && (
                  <div className="pl-4 pt-1">
                    {group.items.map((item) => {
                      const ItemIcon = item.icon;
                      const active = isNavItemActive(location.pathname, item.href);
                      return (
                        <button
                          key={item.href}
                          type="button"
                          onClick={() => navigate(item.href)}
                          className={cn(
                            'flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500',
                            'hover:bg-purple-100/70 dark:hover:bg-purple-500/15',
                            active ? 'bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-200' : 'text-muted-foreground dark:text-slate-400'
                          )}
                          aria-current={active ? 'page' : undefined}
                        >
                          <ItemIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                          <span className="truncate">{item.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
        </nav>
        <div className={cn('flex items-center gap-2 border-t border-border/80 p-3 text-[10px] text-muted-foreground', expanded ? 'justify-between' : 'justify-center')}>
          {expanded && <span className="whitespace-nowrap">MASTER · EU-CENTRAL</span>}
          <Cloud className="h-4 w-4 shrink-0" aria-label="Servizi cloud" />
        </div>
      </div>
    </aside>
  );
}
